// Place info card — shown when you click a POI, road, park, water body or
// building on the map. Data comes straight from the vector tiles, so it works
// for real-world places without any API key.

import { useState, useCallback } from 'react';
import { useNavStore } from '../../stores/navStore';
import { useMapStore } from '../../stores';
import { cn } from '../../lib/utils';
import {
  PlaceIcon,
  RoadIcon,
  WaterIcon,
  ParkIcon,
  MountainIcon,
  BuildingIcon,
  HashIcon,
  AirplaneIcon,
} from './NavIcons';
import {
  X,
  Route as RouteIcon,
  Locate,
  Navigation,
  Copy,
  Check,
  MapPin,
  Layers,
} from 'lucide-react';

export interface PlaceInfo {
  /** 'poi' | 'place' | 'road' | 'water' | 'park' | 'peak' | 'house' | 'building' | 'aerodrome' */
  kind: string;
  name: string;
  /** e.g. "restaurant", "town", "residential" */
  category: string;
  /** more specific, e.g. "pizza", "city", "motorway" */
  subclass?: string;
  /** OSM class hint: 'restaurant', 'shop', 'amenity'... */
  osmClass?: string;
  lng: number;
  lat: number;
  /** optional extras straight from the tile */
  extras?: { label: string; value: string }[];
}

const KIND_META: Record<string, { label: string; tint: string; Icon: React.ComponentType<{ className?: string }> }> = {
  poi: { label: 'Place', tint: 'bg-primary-100 text-primary-700', Icon: PlaceIcon },
  place: { label: 'Area', tint: 'bg-violet-100 text-violet-700', Icon: Layers },
  road: { label: 'Road', tint: 'bg-slate-200 text-slate-700', Icon: RoadIcon },
  water: { label: 'Water', tint: 'bg-sky-100 text-sky-700', Icon: WaterIcon },
  park: { label: 'Park', tint: 'bg-emerald-100 text-emerald-700', Icon: ParkIcon },
  peak: { label: 'Mountain', tint: 'bg-stone-200 text-stone-700', Icon: MountainIcon },
  house: { label: 'Address', tint: 'bg-amber-100 text-amber-800', Icon: HashIcon },
  building: { label: 'Building', tint: 'bg-orange-100 text-orange-800', Icon: BuildingIcon },
  aerodrome: { label: 'Airport', tint: 'bg-indigo-100 text-indigo-700', Icon: AirplaneIcon },
};

export function PlaceInfoCard({
  place,
  onClose,
}: {
  place: PlaceInfo;
  onClose: () => void;
}) {
  const { setDestination, openDirections, setPanel } = useNavStore();
  const { setMapState } = useMapStore();

  const [copied, setCopied] = useState(false);

  const meta = KIND_META[place.kind] ?? {
    label: 'Place',
    tint: 'bg-slate-100 text-slate-700',
    Icon: MapPin,
  };
  const Icon = meta.Icon;

  const handleDirections = useCallback(() => {
    setDestination({
      coords: { lat: place.lat, lng: place.lng },
      label: place.name,
    });
    openDirections();
  }, [place, setDestination, openDirections]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${place.lat.toFixed(6)}, ${place.lng.toFixed(6)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }, [place]);

  const handleZoomIn = useCallback(() => {
    setMapState({ center: { lat: place.lat, lng: place.lng }, zoom: 17 });
  }, [place, setMapState]);

  const subtitle = [place.subclass, place.osmClass].filter(Boolean).join(' · ');

  return (
    <div className="w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            meta.tint
          )}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {meta.label}
          </p>
          <h3 className="mt-0.5 break-words text-sm font-semibold leading-snug text-slate-900">
            {place.name}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-xs capitalize text-slate-500">{subtitle}</p>
          )}
        </div>

        <button
          onClick={onClose}
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close place info"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Extras from the tile data */}
      {place.extras && place.extras.length > 0 && (
        <div className="space-y-1 border-t border-slate-100 px-4 py-2.5">
          {place.extras.map((x) => (
            <div key={x.label} className="flex items-baseline justify-between gap-3">
              <span className="shrink-0 text-[11px] text-slate-400">{x.label}</span>
              <span className="truncate text-[11px] font-medium text-slate-700">
                {x.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Coordinates */}
      <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-2">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="flex-1 font-mono text-[11px] text-slate-500">
          {place.lat.toFixed(5)}, {place.lng.toFixed(5)}
        </span>
        <button
          onClick={handleCopy}
          title="Copy coordinates"
          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <button
          onClick={handleZoomIn}
          title="Zoom in"
          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <Locate className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 p-2.5">
        <button
          onClick={handleDirections}
          className="flex w-full items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          <RouteIcon className="h-4 w-4" />
          Directions to here
        </button>
      </div>
    </div>
  );
}

export default PlaceInfoCard;